'use client';

import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  Grid,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  AssignmentTurnedIn,
  BarChart,
  CheckCircle,
  WarningAmber,
  ArrowForward,
  ExpandMore,
  People,
  FolderOpen,
  Edit,
  VerifiedUser,
  AccountTree,
  CompareArrows,
  TrendingFlat,
  Domain,
  Search,
  Checklist
} from '@mui/icons-material';
import Header from '../../components/HomepageContent/Header';
import FeatureHeroCtaStack from '../components/FeatureHeroCtaStack';
import FeatureResourceButtons from '../components/FeatureResourceButtons';
import Footer from '../../components/HomepageContent/Footer';
import Link from 'next/link';
import WorkflowStepper from '../../components/WorkflowStepper';
import '../features-responsive.css';
import BrandName from '../../components/BrandName';

const BRAND_BLUE = '#0F4C9C';
const BRAND_GREEN = '#63A821';

const workflowSteps = [
  { title: 'Create', description: 'Build policy content using customizable forms' },
  { title: 'Route & Review', description: 'Send through sequential approval paths' },
  { title: 'Approve', description: 'Validate changes with side-by-side diffs' },
  { title: 'Assign', description: 'Target specific users, roles, or committees' },
  { title: 'Acknowledge', description: 'Track attestations for total visibility' }
];

const painPoints = [
  'Policies scattered across drives, email, and paper with no single source of truth',
  'No clear version history or audit trail when policies are revised',
  'Review and approval workflows handled informally over email',
  'Staff acknowledgment tracked on sign-in sheets or not at all',
  'Hard to assign policies to the right roles, users, or committees',
  'Hours lost locating current policies during surveys or internal audits',
];

const capabilities = [
  {
    icon: FolderOpen,
    title: 'Centralized Policy Repository',
    description: 'Create, organize, and manage all facility policies and procedures in one platform—searchable by category, type, and keyword.',
  },
  {
    icon: Checklist,
    title: 'Configurable Policy Forms',
    description: 'Build policy content with the Form Wizard so each policy captures exactly the fields, sections, and structure your organization requires.',
  },
  {
    icon: AccountTree,
    title: 'Category & Type Organization',
    description: 'Organize policies by category and type for fast navigation—whether you have dozens or hundreds of active documents.',
  },
  {
    icon: AssignmentTurnedIn,
    title: 'Multi-Step Review Workflow',
    description: 'Route policies through structured review cycles. Assign reviewers by user, role, or committee—with due dates, priorities, and review reasons.',
  },
  {
    icon: TrendingFlat,
    title: 'Sequential Review Routing',
    description: 'Chain reviewers in order so each policy moves through the right approval path before going live—no more lost email threads.',
  },
  {
    icon: CompareArrows,
    title: 'Side-by-Side Change Comparison',
    description: 'Compare policy versions visually with a side-by-side diff so reviewers see exactly what changed before approving.',
  },
  {
    icon: People,
    title: 'Staff Assignment & Acknowledgment',
    description: 'Assign policies to specific users, roles, or committees and track who has acknowledged receipt—with visibility into pending and completed attestations.',
  },
  {
    icon: BarChart,
    title: 'Review Status Tracking',
    description: 'Monitor policies through their full lifecycle: draft, under review, active, and archived—with clear status at every stage.',
  },
  {
    icon: Domain,
    title: 'Facility-Level Management',
    description: 'Manage policies per facility while supporting consistent standards across a multi-site enterprise.',
  },
  {
    icon: Search,
    title: 'Search, Filter & Preview',
    description: 'Find any policy instantly with keyword search and category/type filters. Preview policy content before assignment or acknowledgment.',
  },
  {
    icon: VerifiedUser,
    title: 'Audit-Ready Documentation',
    description: 'Full review history, assignment records, and acknowledgment logs—ready when surveyors, corporate compliance, or leadership ask for proof.',
  },
];

const roles = [
  {
    title: 'Administrators & DONs',
    benefit: 'Confidence that current policies are assigned, reviewed, and acknowledged',
  },
  {
    title: 'Compliance & Quality Leaders',
    benefit: 'Structured review workflows and complete audit trails',
  },
  {
    title: 'Policy Owners & Authors',
    benefit: 'Guided creation, revision, and routing without email chaos',
  },
  {
    title: 'HR & Training Coordinators',
    benefit: 'Clear visibility into who has acknowledged required policies',
  },
  {
    title: 'All Staff',
    benefit: 'Easy access to assigned policies with a simple acknowledgment workflow',
  },
  {
    title: 'Corporate / Multi-Site Operators',
    benefit: 'Consistent policy management standards across every facility',
  },
];

const outcomes = [
  { value: '1', label: 'Source of truth', detail: 'Current policies always accessible in one place' },
  { value: '100%', label: 'Acknowledgment visibility', detail: 'Know who has—and has not—acknowledged each policy' },
  { value: 'Fast', label: 'Review cycles', detail: 'Structured routing vs. informal email approvals' },
  { value: 'Full', label: 'Audit trail', detail: 'Version history, reviews, and attestations on demand' },
];

const integrations = [
  'Form Wizard',
  'Committee & Meeting Management',
  'Attestation',
  'Due Items',
  'QAPI',
  'Document Vault',
  'Action Plans',
];

const faqs = [
  {
    q: 'Can we configure our own policy templates?',
    a: 'Yes. Policies are built with the Form Wizard, so each facility can define the structure and fields that match organizational standards.',
  },
  {
    q: 'How does the review workflow work?',
    a: 'Policies are assigned to reviewers by user, role, or committee. Each reviewer completes their review and can route to the next reviewer in the chain—with due dates, priorities, and documented comments.',
  },
  {
    q: 'Can we track staff acknowledgments?',
    a: 'Yes. Assign policies to users, roles, or committees and monitor acknowledgment status—with reminders and reporting for pending attestations.',
  },
  {
    q: 'Can we see what changed between policy versions?',
    a: 'Yes. Side-by-side comparison shows exactly what was added, removed, or modified between versions.',
  },
  {
    q: 'Is this only for single facilities?',
    a: 'No. Multi-facility operators can manage policies consistently across their entire organization, facility by facility.',
  },
  {
    q: 'What policy lifecycle stages are supported?',
    a: 'Policies move through defined stages including draft, active, under review, and archived—giving leadership clear visibility at every step.',
  },
];

export default function PolicyAndProcedureClient() {
  const [expanded, setExpanded] = useState<number | false>(0);

  const handleChange = (panel: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  useEffect(() => {
    document.title = 'Policy & Procedure Management Software for SNFs | CareAscendium';
  }, []);

  return (
    <Box className="feature-page-wrapper" sx={{ minHeight: '100vh', bgcolor: '#fafafa', display: 'flex', flexDirection: 'column' }}>
      <Header />

      {/* Hero */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${BRAND_BLUE} 0%, #1E4A8C 50%, #2A5A9C 100%)`,
          py: { xs: 6, md: 8 },
          px: 4,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Chip
            label="Policy & Procedure Management"
            sx={{
              bgcolor: 'rgba(255,255,255,0.15)',
              color: 'white',
              fontWeight: 600,
              mb: 3,
              border: '1px solid rgba(255,255,255,0.3)',
            }}
          />
          <Typography
            variant="h1"
            sx={{
              color: 'white',
              mb: 3,
              fontWeight: 800,
              fontSize: { xs: '2rem', md: '3.25rem' },
              lineHeight: 1.15,
              maxWidth: 940,
              mx: 'auto',
            }}
          >
            Keep Every Policy Current, Reviewed, and Acknowledged—Across Your Entire Organization
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: 'rgba(255,255,255,0.92)',
              maxWidth: 840,
              mx: 'auto',
              lineHeight: 1.6,
              fontWeight: 300,
              fontSize: { xs: '1.05rem', md: '1.35rem' },
              mb: 5,
            }}
          >
            <BrandName variant="dark" style={{ fontSize: 'inherit' }} /> gives skilled nursing facilities a complete policy lifecycle—from creation and multi-step review through staff assignment and acknowledgment tracking—with version control, side-by-side change comparison, and survey-ready documentation.
          </Typography>
          <FeatureHeroCtaStack>
            <Link href="/contact" style={{ textDecoration: 'none' }}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForward />}
                sx={{
                  bgcolor: BRAND_GREEN,
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  fontWeight: 700,
                  borderRadius: '6px',
                  textTransform: 'none',
                  '&:hover': { bgcolor: '#55901d' },
                }}
              >
                REQUEST A DEMO
              </Button>
            </Link>
            <FeatureResourceButtons />
          </FeatureHeroCtaStack>
        </Container>
      </Box>

      {/* Problem */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Grid container spacing={5} alignItems="center" justifyContent="center">
          <Grid size={{ xs: 12, md: 10 }} sx={{ textAlign: 'center' }}>
            <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 3, fontWeight: 700, fontSize: { xs: '1.75rem', md: '2.25rem' } }}>
              The Challenge
            </Typography>
            <Typography sx={{ color: '#666', mb: 4, lineHeight: 1.7, fontSize: '1.05rem', maxWidth: 850, mx: 'auto' }}>
              Policies and procedures are the backbone of compliant long-term care—but in most facilities they still live in shared drives, email attachments, and outdated binders. Revisions get lost. Staff cannot find the current version. Review cycles slip. Administrators struggle to prove who has read and acknowledged updated policies when surveyors or corporate compliance ask.
            </Typography>
            <Box component="ul" sx={{ pl: 0, listStyle: 'none', m: 0, display: 'inline-block', textAlign: 'left' }}>
              {painPoints.map((point) => (
                <Box
                  component="li"
                  key={point}
                  sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5, gap: 1.5 }}
                >
                  <WarningAmber sx={{ color: '#FF9800', mt: 0.2, flexShrink: 0 }} />
                  <Typography sx={{ color: '#555', lineHeight: 1.5 }}>{point}</Typography>
                </Box>
              ))}
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 10 }}>
            <Paper
              elevation={4}
              sx={{
                p: 4,
                borderRadius: 3,
                background: 'linear-gradient(145deg, #F3F1FF 0%, #ffffff 100%)',
                borderLeft: `4px solid ${BRAND_BLUE}`,
              }}
            >
              <Typography
                sx={{
                  fontStyle: 'italic',
                  color: BRAND_BLUE,
                  fontSize: '1.15rem',
                  lineHeight: 1.6,
                  fontWeight: 500,
                }}
              >
                &ldquo;A revised policy that staff haven't read—and haven't acknowledged—isn't a policy. It's a survey citation waiting to happen.&rdquo;
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Solution / Workflow */}
      <Box sx={{ bgcolor: '#f0f4fa', py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 2, fontWeight: 700 }}>
              A Complete Policy Lifecycle
            </Typography>
            <Typography sx={{ color: '#666', maxWidth: 780, mx: 'auto', lineHeight: 1.7, fontSize: '1.05rem' }}>
              <BrandName variant="light" style={{ fontSize: 'inherit' }} /> centralizes policies into a single, searchable repository. Move policies through structured review cycles with side-by-side version comparison, assign them to the right roles, and effortlessly track staff acknowledgments to guarantee facility-wide compliance.
            </Typography>
          </Box>
          <WorkflowStepper steps={workflowSteps} />
        </Container>
      </Box>

      {/* Capabilities */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 2, fontWeight: 700 }}>
            Key Capabilities
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 3,
            alignItems: 'stretch',
          }}
        >
          {capabilities.map((cap) => (
            <Box key={cap.title}>
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  height: '100%',
                  borderRadius: 3,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 28px rgba(21,60,127,0.12)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: '50%',
                    bgcolor: `${BRAND_BLUE}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                  }}
                >
                  <cap.icon sx={{ color: BRAND_BLUE, fontSize: 26 }} />
                </Box>
                <Typography variant="h6" sx={{ color: BRAND_BLUE, fontWeight: 700, mb: 1.5 }}>
                  {cap.title}
                </Typography>
                <Typography sx={{ color: '#666', mb: 2, lineHeight: 1.6, fontSize: '0.95rem' }}>
                  {cap.description}
                </Typography>
              </Paper>
            </Box>
          ))}
        </Box>
      </Container>

      {/* Roles + Integration */}
      <Box sx={{ bgcolor: '#f0f4fa', py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={5} justifyContent="center" alignItems="stretch">
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 3, fontWeight: 700, fontSize: '1.75rem' }}>
                Who Benefits
              </Typography>
              {roles.map((role) => (
                <Box key={role.title} sx={{ display: 'flex', gap: 1.5, mb: 2.5 }}>
                  <CheckCircle sx={{ color: BRAND_GREEN, mt: 0.2, flexShrink: 0 }} />
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: BRAND_BLUE }}>{role.title}</Typography>
                    <Typography sx={{ color: '#666', fontSize: '0.95rem', lineHeight: 1.5 }}>
                      {role.benefit}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper
                elevation={3}
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: 3,
                  height: '100%',
                  width: '100%',
                  background: `linear-gradient(145deg, ${BRAND_BLUE} 0%, #1E4A8C 100%)`,
                  color: 'white',
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, fontSize: '1.5rem' }}>
                  Part of the <BrandName variant="dark" style={{ fontSize: 'inherit' }} /> Platform
                </Typography>
                <Typography sx={{ color: '#FFFFFF', fontWeight: 500, mb: 3, lineHeight: 1.6 }}>
                  Policy & Procedure Management ties seamlessly into the rest of our compliance tools:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3, justifyContent: 'center' }}>
                  {integrations.map((item) => (
                    <Chip
                      key={item}
                      label={item}
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.15)',
                        color: 'white',
                        fontWeight: 600,
                        border: '1px solid rgba(255,255,255,0.25)',
                      }}
                    />
                  ))}
                </Box>
                <Typography sx={{ fontWeight: 600, color: '#a8d86a', fontStyle: 'italic' }}>
                  One platform. One source of truth. Absolute audit-readiness.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Outcomes & Compliance */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 2, fontWeight: 700 }}>
            Outcomes
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: 3,
            mb: 8,
            alignItems: 'stretch',
            maxWidth: 1000,
            mx: 'auto'
          }}
        >
          {outcomes.map((item) => (
            <Paper
              key={item.label}
              elevation={2}
              sx={{
                p: 3,
                textAlign: 'center',
                borderRadius: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: BRAND_GREEN, mb: 0.5 }}>
                {item.value}
              </Typography>
              <Typography sx={{ fontWeight: 700, color: BRAND_BLUE, mb: 0.5, fontSize: '0.95rem' }}>
                {item.label}
              </Typography>
              <Typography sx={{ color: '#888', fontSize: '0.85rem' }}>{item.detail}</Typography>
            </Paper>
          ))}
        </Box>
      </Container>

      {/* FAQ */}
      <Box sx={{ py: { xs: 10, md: 14 }, px: 4, background: 'linear-gradient(113.99deg, #153C7F 12.55%, #000820 87.43%)' }}>
        <Container maxWidth="md">
          <Typography variant="h3" sx={{ fontWeight: 800, color: 'white', mb: 6, textAlign: 'center', fontSize: { xs: '1.75rem', md: '2.5rem' } }}>
            FAQs
          </Typography>
          <Box>
            {faqs.map((faq, i) => (
              <Accordion 
                key={i}
                expanded={expanded === i} 
                onChange={handleChange(i)}
                sx={{ 
                  mb: 2.5, 
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '12px !important',
                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.08)'
                  },
                  '&:before': { display: 'none' }
                }}
              >
                <AccordionSummary expandIcon={<ExpandMore sx={{ color: 'white' }} />} sx={{ px: { xs: 2, md: 4 }, py: 1.5 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', letterSpacing: '0.02em' }}>{faq.q}</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: { xs: 2, md: 4 }, pb: 4 }}>
                  <Typography sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.8, fontSize: '1.05rem' }}>
                    {faq.a}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA */}
      <Box sx={{ py: 6, bgcolor: '#f0f4fa' }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              backgroundImage: `url('/assets/osha/bgdemo.svg'), linear-gradient(113.99deg, #0F4C9C 12.55%, #000820 87.43%)`,
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              borderRadius: '16px',
              minHeight: '300px',
              py: 5,
              px: 3,
              textAlign: 'center',
              color: 'white',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 15px 40px rgba(0, 8, 32, 0.12)',
            }}
          >
            <Typography variant="h2" sx={{ mb: 2, fontWeight: 800, fontSize: { xs: '1.75rem', md: '2.5rem' }, textTransform: 'uppercase' }}>
              Ready to take control of your policies?
            </Typography>
            <Typography sx={{ mb: 4, opacity: 0.9, lineHeight: 1.6, fontSize: '1.1rem', maxWidth: 850 }}>
              Discover how our centralized platform enforces compliance, speeds up review cycles, and tracks staff acknowledgments with a complete audit trail.
            </Typography>
            <Link href="/contact" style={{ textDecoration: 'none' }}>
              <Button
                variant="contained"
                size="large"
                sx={{
                  bgcolor: BRAND_GREEN,
                  color: 'white',
                  px: 5,
                  py: 1.5,
                  fontWeight: 700,
                  borderRadius: '30px',
                  textTransform: 'none',
                  fontSize: '1.1rem',
                  '&:hover': { bgcolor: '#528a1b' },
                  boxShadow: '0 4px 10px rgba(99, 168, 33, 0.2)',
                }}
              >
                REQUEST A DEMO
              </Button>
            </Link>
          </Box>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}
